// src/services/apiService.js
import { supabase } from '../supabaseClient';
class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    this.defaultTimeout = 30000; // 30 seconds for normal operations
    this.longTimeout = 180000; // 3 minutes for long operations (AI, deploy)
  }

  // Get current session token
  async getAuthToken() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  async request(endpoint, options = {}) {
    // Determine timeout based on operation type
    const isLongOperation = this.isLongOperation(endpoint);
    const timeout = isLongOperation ? this.longTimeout : this.defaultTimeout;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Get auth token for protected routes
      const token = await this.getAuthToken();
      
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      // Add authorization header if token exists
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || response.statusText;
        } catch (parseError) {
          errorMessage = errorText || response.statusText;
        }
        
        throw new Error(`Request failed: ${response.status} - ${errorMessage}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutMessage = isLongOperation 
          ? 'Operation timeout (3 minutes) - the server might be busy. Please try again.'
          : 'Request timeout - please check your connection and try again';
        throw new Error(timeoutMessage);
      }
      
      if (error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }
      
      throw error;
    }
  }

  // Helper to identify long-running operations
  isLongOperation(endpoint) {
    const longOperations = [
      '/sites/ai',              // AI site creation
      '/sites/regenerate-static', // Regeneration
      '/sites/deploy',          // Netlify deploy
      '/sites/deploy-aws',      // AWS deploy
      '/sites/redeploy-netlify', // Redeploy
      '/sites/redeploy-aws'     // Redeploy
    ];
    
    // Delete operations are quick, so they're not included here
    return longOperations.some(op => endpoint.includes(op));
  }

  // Site operations with custom timeouts
  async createSite(siteData) {
    // AI site creation - can take 1-3 minutes
    return this.request('/sites/ai', {
      method: 'POST',
      body: JSON.stringify(siteData)
    });
  }

  async getSites(userId) {
    // Quick operation - 30 second timeout
    return this.request(`/sites/${userId}`);
  }

  async updateSite(siteId, updateData) {
    // Quick operation - 30 second timeout
    return this.request(`/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  async regenerateSite(siteId) {
    // Long operation - 3 minute timeout
    return this.request('/sites/regenerate-static', {
      method: 'POST',
      body: JSON.stringify({ siteId })
    });
  }

  async deploySite(siteId, platform) {
    // Long operation - 3 minute timeout
    const endpoint = platform === 'netlify' ? '/sites/deploy' : '/sites/deploy-aws';
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ siteId })
    });
  }

  async redeploySite(siteId, platform) {
    // Long operation - 3 minute timeout
    const endpoint = platform === 'netlify' ? '/sites/redeploy-netlify' : '/sites/redeploy-aws';
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ siteId })
    });
  }

  // Delete operations
  async deleteSite(siteId) {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Valid site ID is required');
    }
    
    // Quick operation - 30 second timeout
    return this.request(`/sites/${siteId}`, {
      method: 'DELETE'
    });
  }

  // In your apiService.js file, replace the deleteSites method with this:

async deleteSites(siteIds) {
  if (!Array.isArray(siteIds) || siteIds.length === 0) {
    throw new Error('Array of site IDs is required');
  }
  
  if (siteIds.length > 50) {
    throw new Error('Cannot delete more than 50 sites at once');
  }
  
  // Validate and clean site IDs
  const cleanedSiteIds = siteIds.map(id => {
    // Ensure ID is a string and trim whitespace
    const cleanId = typeof id === 'string' ? id.trim() : String(id).trim();
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(cleanId)) {
      throw new Error(`Invalid site ID format: ${cleanId}`);
    }
    
    return cleanId;
  });
  
  console.log('Sending bulk delete request for IDs:', cleanedSiteIds);
  
  // Quick operation - 30 second timeout
  return this.request('/sites/bulk-delete', {
    method: 'DELETE',
    body: JSON.stringify({ siteIds: cleanedSiteIds })
  });
}

  async testCredentials(type, credentials) {
    // Quick operation - 30 second timeout
    return this.request('/test-credentials', {
      method: 'POST',
      body: JSON.stringify({ type, credentials })
    });
  }
}

export const apiService = new ApiService();